// import components
import { Skeleton } from '@mui/material';
import { StyledRelativeBox } from 'components/shared/Box/Box.styles';
import NoteCard from './NoteCard';
import {
  StyledIndicatorBox,
  StyledIndicatorEllipsis
} from './NoteSlider.styles';

// import hooks
import useNoteSliderNavigation from '../hooks/useNoteSliderNavigation';

// import types
import { NoteSliderProps } from './NoteSlider.types';

// import utils
import {
  MotionCardSliderAnimation,
  MotionSliderIndicators,
  MotionSliderNavButton
} from './NoteSlider.motion';
import { NOTE_VARIANT } from '../Notes.consts';
import { t } from 'i18next';

export default function NoteSlider({
  notes,
  isFetching,
  onDelete,
  deletingId,
  isEmployeePortal = false,
  noteVariant = NOTE_VARIANT.DEFAULT,
  isCompact = false,
  showTooltip = false
}: NoteSliderProps) {
  const {
    containerRef,
    direction,
    safePage,
    setPage,
    totalNotes,
    hasHiddenIndicatorsOnLeft,
    hasHiddenIndicatorsOnRight,
    hiddenIndicatorsOnLeftCount,
    hiddenIndicatorsOnRightCount,
    visibleIndicatorIndices,
    paginate,
    jumpToPage,
    handleKeyDown,
    current,
    currentId
  } = useNoteSliderNavigation({ notes, isCompact });

  if (isFetching) return <Skeleton variant="rectangular" height={160} />;
  if (!current || currentId === null) return null;

  return (
    <StyledRelativeBox
      ref={containerRef}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      aria-label={t('notesSliderAriaLabel')}
    >
      {/* slider viewport */}
      <MotionCardSliderAnimation currentId={currentId} direction={direction}>
        <NoteCard
          note={current}
          deleting={deletingId === currentId}
          onDelete={() => onDelete(currentId)}
          isEmployeePortal={isEmployeePortal}
          noteVariant={noteVariant}
          isCompact={isCompact}
          showTooltip={showTooltip}
        />
      </MotionCardSliderAnimation>
      {/* nav buttons */}
      {totalNotes > 1 && (
        <>
          <MotionSliderNavButton
            onClick={() => paginate(-1)}
            position="left"
            iconType="chevron-left"
            ariaLabel={t('notesSliderPreviousNoteAriaLabel')}
          />
          <MotionSliderNavButton
            onClick={() => paginate(1)}
            position="right"
            iconType="chevron-right"
            ariaLabel={t('notesSliderNextNoteAriaLabel')}
          />

          {/* indicator strip */}
          <StyledIndicatorBox isCompact={isCompact}>
            {hasHiddenIndicatorsOnLeft && (
              <StyledIndicatorEllipsis
                type="button"
                onClick={() => jumpToPage(0)}
                isCompact={isCompact}
                aria-label={t('notesSliderGoToFirstNoteHiddenAriaLabel', {
                  count: hiddenIndicatorsOnLeftCount
                })}
                title={t('notesSliderGoToFirstNoteHiddenTitle', {
                  count: hiddenIndicatorsOnLeftCount
                })}
              >
                +{hiddenIndicatorsOnLeftCount}
              </StyledIndicatorEllipsis>
            )}

            {visibleIndicatorIndices.map((absoluteIndex) => (
              <MotionSliderIndicators
                key={absoluteIndex}
                index={absoluteIndex}
                setPage={setPage}
                safePage={safePage}
              />
            ))}

            {hasHiddenIndicatorsOnRight && (
              <StyledIndicatorEllipsis
                type="button"
                onClick={() => jumpToPage(totalNotes - 1)}
                isCompact={isCompact}
                aria-label={t('notesSliderGoToLastNoteHiddenAriaLabel', {
                  count: hiddenIndicatorsOnRightCount
                })}
                title={t('notesSliderGoToLastNoteHiddenTitle', {
                  count: hiddenIndicatorsOnRightCount
                })}
              >
                +{hiddenIndicatorsOnRightCount}
              </StyledIndicatorEllipsis>
            )}
          </StyledIndicatorBox>
        </>
      )}
    </StyledRelativeBox>
  );
}
